<template>
  <nav id="main-navigation" :class="`navigation-bar ${mobileNavOpen ? 'mobile-nav-open' : ''}`">
    <!-- OP Koti Logo -->
    <RouterLink :id="config.testing.NAVIGATION.MAIN_NAV_LOGO" class="home-link" to="/" tabindex="0">
      <img src="https://op-koti.fi/assets/op-koti-logo.png" alt="OP Koti" />
    </RouterLink>

    <!-- Main nav links -->
    <ul :class="isExpanded" class="main-navigation-links">
      <navigation-subnav
        v-for="link in navigationLinks"
        :key="link.id"
        :id="link.id"
        :name="link.name"
        :pathRef="link.pathRef"
        :routes="link.routes"
        :navUrl="link.navUrl"
        :ariaLabel="link.ariaLabel"
      ></navigation-subnav>

      <li class="navigation-list-item">
        <a
          :class="`navigation-link main-nav-contact ${areWeInContactPage(contactPage.path) ? 'RouterLink-active' : ''}`"
          :href="contactPage.path + '#contact'"
          @click.prevent="navigateToContactPage({ path: contactPage.path, hash: '#contact' })"
          tabindex="0"
          :aria-current="areWeInContactPage(contactPage.path) ? 'page' : undefined"
        >
          <i class="icon-message"></i>
          <span>Ota yhteyttä</span>
        </a>
      </li>
    </ul>

    <!-- Mobile burger icon -->
    <div class="mobile-nav-button">
      <button
        id="mobile-nav-button"
        aria-label="Navigaatio"
        :class="menuButtonClass"
        class="burger-menu"
        @click="toggleMobileNav"
        tabindex="0"
      >
        <div class="burger-icon" :class="menuButtonClass">
          <span class="burger-icon__bar"></span>
          <span class="burger-icon__bar"></span>
          <span class="burger-icon__bar"></span>
        </div>
      </button>
    </div>
  </nav>
</template>

<script lang="ts">
import config from '@/config';
import NavigationLink from '../navigation-link/navigation-link.vue';
import * as routes from '@/app/routes';
import NavigationSubnavItem from './navigation-subnav-item.vue';
import NavigationSubnav from './navigation-subnav.vue';
import { selling, search, embeddable, contactPage } from '@/app/routes';
import utils from '@/app/utils/utils';
import isMobile from '@/utils/isMobile';
import navigationLinks from '@/app/navigationLinks';
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'navigation',
  data: () => ({
    navigationLinks: navigationLinks,
    mobileNavOpen: false,
    config,
    subNavOpen: false,
    onSaleSearchSelected: false,
    rentalsSearchSelected: false,
    weAreInMobile: false,
    routes,
    selling,
    search,
    embeddable,
    contactPage,
    keys: {
      mode: 'mode',
      featureGroup: 'featureGroup',
    },
    _focusTrapHandler: null as ((e: KeyboardEvent) => void) | null,
  }),
  computed: {
    isExpanded() {
      return this.mobileNavOpen ? 'is-expanded' : '';
    },
    menuButtonClass() {
      return this.mobileNavOpen ? 'close' : '';
    },
  },
  methods: {
    handleKeydown(event: KeyboardEvent) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        if (this.mobileNavOpen) {
          this.mobileNavOpen = false;
        }
      }
    },
    trapFocus() {
      const navigation = document.getElementById('main-navigation');
      if (!navigation) {
        return;
      }
      const focusableElements = navigation.querySelectorAll('a, i, [tabindex]:not([tabindex="-1"])');
      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      const handleFocus = (e: KeyboardEvent) => {
        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      };

      navigation.addEventListener('keydown', handleFocus);

      firstElement.focus();

      this._focusTrapHandler = handleFocus;
    },
    releaseFocus() {
      const navigation = document.getElementById('main-navigation');
      if (this._focusTrapHandler) {
        if (navigation) {
          navigation.removeEventListener('keydown', this._focusTrapHandler);
        }
        this._focusTrapHandler = null;
      }
    },
    navigateUrl(link: any) {
      void this.$router.push(link.href);
    },
    toggleMobileNav() {
      this.mobileNavOpen = !this.mobileNavOpen;
    },
    offClick(event: MouseEvent) {
      if (!this.$el.contains(event.target)) {
        this.mobileNavOpen = false;
      }
    },
    navigateToContactPage(href: object | string) {
      this.mobileNavOpen = false;
      void this.$router.push(href);
    },
    areWeInContactPage(path: string) {
      return this.$route.path.includes(utils.stripQuery(path));
    },
  },
  components: {
    NavigationSubnav,
    NavigationSubnavItem,
    NavigationLink,
  },
  beforeUnmount() {
    document.removeEventListener('click', this.offClick);
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeydown);

    document.addEventListener('click', (event) => {
      this.offClick(event);
    });

    this.$bus.$on(
      'openMainNavLink',
      (link) => {
        this.navigateUrl(link);
        this.mobileNavOpen = false;
      },
      this
    );

    this.weAreInMobile = isMobile();
    this.$bus.$on(
      'windowResized',
      () => {
        this.weAreInMobile = isMobile();
      },
      this
    );
  },
  watch: {
    mobileNavOpen(newValue) {
      if (newValue) {
        this.trapFocus();
      } else {
        this.releaseFocus();
      }
    },
  },
});
</script>
